import { useCallback, useEffect, useState } from 'react'

interface UseModalFormCloseOptions {
  /** Whether the form has unsaved changes. */
  isDirty: boolean
  /** Whether a save/submit is in progress. */
  isSubmitting: boolean
  /** Set to false for read-only / view modals that have no editable form. */
  hasForm?: boolean
  /**
   * Whether to show confirmation dialog when closing with unsaved changes.
   * Defaults to false (off by default, only enabled for hazardous operations).
   */
  confirmOnClose?: boolean
  /** Called when the user confirms they want to discard changes (or when closing is allowed). */
  onConfirmClose: () => void
}

interface UseModalFormCloseReturn {
  /** Unified close handler — wire this to onCancel, mask click, ESC, X button, etc. */
  handleClose: () => void
  /** Whether the "discard changes?" confirmation dialog is visible. */
  isConfirmOpen: boolean
  /** User chose "Đóng" — discard and close. */
  handleConfirmOk: () => void
  /** User chose "Tiếp tục chỉnh sửa" — keep the modal open. */
  handleConfirmCancel: () => void
  /** Pass to Modal's `mask.closable` prop. */
  maskClosable: boolean
  /** Pass to Modal's `keyboard` prop (ESC key). */
  keyboard: boolean
  /** Pass to Modal's `closable` prop (X button). */
  closable: boolean
}

/**
 * Centralise all "close modal" logic for form modals.
 *
 * Rules:
 *  - Submitting → block all close attempts.
 *  - confirmOnClose is false (default) → close immediately (onConfirmClose).
 *  - confirmOnClose is true:
 *      - No form / not dirty → close immediately.
 *      - Dirty → show confirmation dialog.
 *
 * Every closing action (Cancel button, X, ESC, mask click) MUST call
 * `handleClose()` so the guard logic is applied consistently.
 */
export function useModalFormClose({
  isDirty,
  isSubmitting,
  hasForm = true,
  confirmOnClose = false,
  onConfirmClose,
}: UseModalFormCloseOptions): UseModalFormCloseReturn {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  const handleClose = useCallback(() => {
    // Rule: Block all close attempts while submitting.
    if (isSubmitting) return

    // If confirmation is not requested, or no form, or not dirty → close immediately.
    if (!confirmOnClose || !hasForm || !isDirty) {
      onConfirmClose()
      return
    }

    // Dangerous/guarded form with unsaved changes → show confirmation dialog.
    setIsConfirmOpen(true)
  }, [confirmOnClose, isDirty, isSubmitting, hasForm, onConfirmClose])

  const handleConfirmOk = useCallback(() => {
    setIsConfirmOpen(false)
    onConfirmClose()
  }, [onConfirmClose])

  const handleConfirmCancel = useCallback(() => {
    setIsConfirmOpen(false)
  }, [])

  // When submitting finishes (isSubmitting goes from true → false),
  // automatically close the confirm dialog if it was somehow open.
  useEffect(() => {
    if (!isSubmitting) return
    setIsConfirmOpen(false)
  }, [isSubmitting])

  return {
    handleClose,
    isConfirmOpen,
    handleConfirmOk,
    handleConfirmCancel,
    // When confirmOnClose is true, disable mask click & ESC while dirty or submitting.
    // When confirmOnClose is false, allow mask click & ESC unless submitting.
    maskClosable: confirmOnClose ? !isDirty && !isSubmitting : !isSubmitting,
    keyboard: confirmOnClose ? !isDirty && !isSubmitting : !isSubmitting,
    // While submitting, hide the X button entirely.
    closable: !isSubmitting,
  }
}
